import { useRef, useCallback, useEffect } from "react";

interface UseInfiniteScrollProps {
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
  options?: IntersectionObserverInit;
}

export const useInfiniteScroll = ({
  isFetching,
  hasMore,
  loadMore,
  options,
}: UseInfiniteScrollProps): ((node: HTMLElement | null) => void) => {
  const observer = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasMore) {
            loadMore();
          }
        },
        { threshold: 0.8, ...options }
      );
      if (node) observer.current.observe(node);
    },
    [isFetching, hasMore, loadMore, options]
  );

  useEffect(() => {
    const currentObserver = observer.current;
    return () => {
      if (currentObserver) {
        currentObserver.disconnect();
      }
    };
  }, []);

  return ref;
};
